/**
 * Memories page — a feed of named memory events. As of the
 * memories-only iteration, the raw photo timeline lives behind the
 * memory cards rather than alongside them. Photos themselves are
 * still accessible via:
 *   - tapping a thumb inside a memory's detail page → /memories/:id
 *   - the composer modal's photo picker (multi-select from the
 *     family's photo pool)
 *
 * The header's "+" menu has two items: "Add photo" uploads into the
 * pool (new uploads become selectable in the composer but don't render
 * on this page until tied into a memory), and "New memory" opens the
 * composer.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, photoBytesUrl } from '../lib/api';
import {
  memoriesListQueryOptions,
  photoGridQueryOptions,
  type MemoryListItem,
  type PhotoGridResponse,
  type PhotoListItem,
} from '../lib/queries';
import { ScreenHeader } from '../components/ScreenHeader';
import { IconButton } from '../components/IconButton';
import { Button } from '../components/Button';
import { IconBookmark, IconImage, IconPlus, IconXCircle } from '../components/icons';
import { MentionTextarea } from '../components/MentionTextarea';
import { TextField } from '../components/TextField';
import { uploadPhoto, type UploadResult } from '../lib/photo-upload';

interface Photo extends PhotoListItem {
  pending?: boolean;
}

const MONTH_LABEL = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

export default function MemoriesPage() {
  const qc = useQueryClient();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [composerOpen, setComposerOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  // Escape closes the add menu (outside taps are caught by the backdrop).
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setMenuOpen(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [menuOpen]);

  const memories = useQuery(memoriesListQueryOptions());

  const upload = useUploadPhoto(() => {
    void qc.invalidateQueries({ queryKey: ['photo-grid'] });
  });

  // Group memories by YYYY-MM, preserving the API's newest-first order.
  const monthGroups = useMemo(() => {
    const order: string[] = [];
    const byKey = new Map<string, MemoryListItem[]>();
    for (const m of memories.data?.memories ?? []) {
      const key = m.eventDate
        ? m.eventDate.slice(0, 7)
        : new Date(m.createdAt).toISOString().slice(0, 7);
      if (!byKey.has(key)) {
        byKey.set(key, []);
        order.push(key);
      }
      byKey.get(key)!.push(m);
    }
    return order.map((key) => ({ key, memories: byKey.get(key)! }));
  }, [memories.data]);

  return (
    <>
      <ScreenHeader
        title="Memories"
        right={
          <div className="relative">
            <IconButton
              icon={<IconPlus />}
              label="Add"
              onClick={() => setMenuOpen((v) => !v)}
            />
            {menuOpen && (
              <>
                {/* Transparent catcher so any outside tap closes the menu. */}
                <button
                  type="button"
                  aria-label="Close menu"
                  className="fixed inset-0 z-20 cursor-default"
                  onClick={() => setMenuOpen(false)}
                />
                <div role="menu" className="absolute right-0 mt-1 w-44 card p-1 z-30">
                  <MenuItem
                    icon={<IconImage className="text-soft shrink-0" />}
                    label="Add photo"
                    onClick={() => {
                      setMenuOpen(false);
                      inputRef.current?.click();
                    }}
                  />
                  <MenuItem
                    icon={<IconBookmark className="text-soft shrink-0" />}
                    label="New memory"
                    onClick={() => {
                      setMenuOpen(false);
                      setComposerOpen(true);
                    }}
                  />
                </div>
              </>
            )}
          </div>
        }
      />
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={async (e) => {
          const files = Array.from(e.target.files ?? []);
          if (e.target) e.target.value = '';
          const queue = [...files];
          const workers = Array.from({ length: Math.min(3, queue.length) }, async () => {
            while (queue.length) {
              const f = queue.shift();
              if (!f) return;
              await upload.mutateAsync({ file: f }).catch(console.error);
            }
          });
          await Promise.all(workers);
        }}
      />

      <div className="px-5 pt-4 pb-6">
        {memories.isLoading ? (
          <p className="text-soft text-sm py-10 text-center">Loading…</p>
        ) : monthGroups.length === 0 ? (
          <EmptyState onCompose={() => setComposerOpen(true)} />
        ) : (
          monthGroups.map((g) => (
            <section key={g.key} className="mb-5">
              <h3 className="sticky top-0 z-10 bg-paper py-2 font-serif text-lg text-ink">
                {monthLabel(g.key)}
              </h3>
              <div className="space-y-2">
                {g.memories.map((m) => (
                  <MemoryCard key={m.id} memory={m} />
                ))}
              </div>
            </section>
          ))
        )}
      </div>

      {composerOpen && (
        <MemoryComposer
          onClose={() => setComposerOpen(false)}
          onSaved={() => setComposerOpen(false)}
        />
      )}
    </>
  );
}

function MenuItem({
  icon,
  label,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-ink hover:bg-line/60 text-left"
    >
      {icon}
      {label}
    </button>
  );
}

function monthLabel(yyyyMm: string): string {
  const [y, m] = yyyyMm.split('-').map(Number);
  if (!y || !m) return yyyyMm;
  return `${MONTH_LABEL[m - 1]} ${y}`;
}

function EmptyState({ onCompose }: { onCompose: () => void }) {
  return (
    <div className="text-center py-12">
      <h2 className="font-serif text-2xl text-ink">No memories yet</h2>
      <p className="font-serif italic text-soft mt-2 max-w-xs mx-auto">
        Group your photos into memories — a trip, a season, a moment worth remembering.
      </p>
      <Button onClick={onCompose} className="mt-6">
        Create your first memory
      </Button>
    </div>
  );
}

function MemoryCard({ memory }: { memory: MemoryListItem }) {
  return (
    <Link
      to={`/memories/m/${memory.id}`}
      className="card flex gap-3 p-2 items-center hover:bg-line/40"
    >
      {memory.coverPhotoId ? (
        <img
          src={photoBytesUrl(memory.coverPhotoId, 'thumb')}
          alt=""
          className="w-20 h-20 object-cover rounded-md bg-line shrink-0"
          loading="lazy"
          decoding="async"
        />
      ) : (
        <div className="w-20 h-20 bg-line rounded-md shrink-0" />
      )}
      <div className="flex-1 min-w-0">
        <h4 className="font-serif text-lg text-ink leading-snug line-clamp-2">
          {memory.title}
        </h4>
        <p className="text-xs text-muted mt-1">{memory.photoCount} photos</p>
        {memory.description && (
          <p className="text-xs text-soft mt-1 line-clamp-2">{memory.description}</p>
        )}
      </div>
    </Link>
  );
}

// -------------------------------------------------------- Composer modal
//
// Title-first composer with an inline multi-select photo picker
// sourced from the family's photo pool. Upload new photos right here
// (they auto-select), or tap existing ones to add/remove them from the
// set. Save creates the memory and invalidates the list.
function MemoryComposer({
  onClose,
  onSaved,
}: {
  onClose: () => void;
  onSaved: () => void;
}) {
  const qc = useQueryClient();
  const grid = useQuery(photoGridQueryOptions(null));
  const allPhotos: Photo[] = useMemo(() => grid.data?.photos ?? [], [grid.data?.photos]);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [descriptionMentions, setDescriptionMentions] = useState<string[]>([]);
  const [eventDate, setEventDate] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [dateTouched, setDateTouched] = useState(false);

  // Upload straight from the composer. New photos land in the family
  // pool (like the page-level "+ Add photo") AND auto-select into this
  // memory, so the flow is "add photos to this memory" rather than the
  // old "upload first, then come back and pick."
  const uploadInto = useMutation<UploadResult, Error, File, UploadContext>({
    onMutate: async (file) => {
      const tempId = `tmp_${crypto.randomUUID?.() ?? Math.random().toString(36).slice(2)}`;
      const blobUrl = URL.createObjectURL(file);
      await qc.cancelQueries({ queryKey: ['photo-grid'] });
      qc.setQueryData<PhotoGridResponse>(['photo-grid', null], (old) => ({
        photos: [
          { id: tempId, url: blobUrl, takenAt: Date.now(), pending: true },
          ...(old?.photos ?? []),
        ],
        nextCursor: old?.nextCursor ?? null,
      }));
      return { tempId, blobUrl };
    },
    mutationFn: (file) => uploadPhoto(file, { type: 'family' }),
    onSuccess: (result, _file, ctx) => {
      if (ctx) URL.revokeObjectURL(ctx.blobUrl);
      // Add the freshly uploaded (or deduped) photo to this memory's set.
      setSelectedIds((prev) => new Set(prev).add(result.photoId));
      void qc.invalidateQueries({ queryKey: ['photo-grid'] });
    },
    onError: (_err, _file, ctx) => {
      if (!ctx) return;
      qc.setQueryData<PhotoGridResponse>(['photo-grid', null], (old) => ({
        photos: (old?.photos ?? []).filter((p) => p.id !== ctx.tempId),
        nextCursor: old?.nextCursor ?? null,
      }));
      URL.revokeObjectURL(ctx.blobUrl);
    },
  });

  const selected: Photo[] = useMemo(
    () => allPhotos.filter((p) => selectedIds.has(p.id) && !p.pending),
    [allPhotos, selectedIds],
  );

  // Auto-default the event date to the earliest takenAt across the
  // current selection — until the user manually edits it.
  const suggestedDate = useMemo(() => {
    const stamps = selected
      .map((p) => p.takenAt)
      .filter((t): t is number => typeof t === 'number')
      .sort((a, b) => a - b);
    if (stamps.length === 0) return '';
    return new Date(stamps[0]!).toISOString().slice(0, 10);
  }, [selected]);
  const effectiveDate = dateTouched ? eventDate : suggestedDate;

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const create = useMutation({
    mutationFn: () =>
      api.post<{ id: string }>('/memories', {
        title: title.trim(),
        description: description.trim() ? description.trim() : null,
        mentions: descriptionMentions.length ? descriptionMentions : undefined,
        eventDate: effectiveDate || undefined,
        photoIds: selected.map((p) => p.id),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['memories', 'list'] });
      onSaved();
    },
  });

  const canSave = title.trim().length > 0 && selected.length > 0 && !create.isPending;

  return (
    <div
      className="fixed inset-0 z-40 bg-ink/40 flex items-end sm:items-center justify-center"
      onClick={onClose}
    >
      <div
        className="w-full sm:max-w-lg bg-paper rounded-t-2xl sm:rounded-2xl max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)' }}
      >
        <header className="px-5 pt-5 pb-3 sticky top-0 bg-paper border-b border-line z-10 flex items-center justify-between">
          <h2 className="font-serif text-xl text-ink">New memory</h2>
          <IconButton icon={<IconXCircle />} label="Close" onClick={onClose} />
        </header>
        <div className="px-5 py-5 space-y-4">
          <div>
            <label className="kicker text-sage mb-1 block">Title</label>
            <TextField
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={120}
              placeholder="Yellowstone, summer '26"
            />
          </div>
          <div>
            <label className="kicker text-sage mb-1 block">
              Description <span className="text-muted lowercase">(optional)</span>
            </label>
            <MentionTextarea
              value={description}
              onChange={(v, m) => {
                setDescription(v);
                setDescriptionMentions(m);
              }}
              maxLength={2000}
              rows={3}
              placeholder="A line or two… @mention someone or @myfamily to notify them."
              className="w-full p-3 rounded-xl border border-line bg-card text-ink placeholder:text-muted focus:outline-none focus:border-oxblood resize-none"
            />
          </div>
          <div>
            <label className="kicker text-sage mb-1 block">Event date</label>
            <TextField
              type="date"
              value={effectiveDate}
              onChange={(e) => {
                setDateTouched(true);
                setEventDate(e.target.value);
              }}
            />
            <p className="text-xs text-muted mt-1">
              Defaults to the earliest selected photo's date.
            </p>
          </div>
          <div>
            <label className="kicker text-sage mb-2 block">
              {selected.length === 0
                ? 'Add photos'
                : `${selected.length} ${selected.length === 1 ? 'photo' : 'photos'} selected`}
            </label>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={(e) => {
                const files = Array.from(e.target.files ?? []);
                if (e.target) e.target.value = '';
                for (const f of files) uploadInto.mutate(f);
              }}
            />
            {grid.isLoading ? (
              <p className="text-soft text-sm italic">Loading your library…</p>
            ) : (
              <div className="grid grid-cols-4 gap-2 max-h-[40vh] overflow-y-auto">
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="aspect-square w-full rounded-md border-2 border-dashed border-line text-soft flex flex-col items-center justify-center gap-1 hover:border-oxblood hover:text-oxblood transition-colors"
                  aria-label="Upload photos"
                >
                  <IconImage />
                  <span className="text-[10px] leading-none">Upload</span>
                </button>
                {allPhotos.map((p) => {
                  const isSelected = selectedIds.has(p.id);
                  if (p.pending) {
                    return (
                      <div
                        key={p.id}
                        className="aspect-square w-full rounded-md bg-line relative"
                      />
                    );
                  }
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => toggleSelect(p.id)}
                      aria-pressed={isSelected}
                      className={`aspect-square w-full overflow-hidden rounded-md bg-line block relative ${
                        isSelected ? 'ring-4 ring-oxblood' : ''
                      }`}
                    >
                      <img
                        src={photoBytesUrl(p.id, 'thumb')}
                        alt=""
                        className="w-full h-full object-cover"
                        loading="lazy"
                        decoding="async"
                      />
                      <span
                        aria-hidden
                        className={`absolute top-1 right-1 w-5 h-5 rounded-full border-2 flex items-center justify-center text-xs ${
                          isSelected
                            ? 'bg-oxblood border-oxblood text-paper'
                            : 'bg-paper/80 border-paper'
                        }`}
                      >
                        {isSelected ? '✓' : ''}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          {create.isError && (
            <p className="text-sm text-oxblood">
              Couldn't save: {(create.error as Error)?.message ?? 'unknown error'}
            </p>
          )}
          <Button
            type="button"
            block
            onClick={() => create.mutate()}
            disabled={!canSave}
            busy={create.isPending}
          >
            Save memory
          </Button>
        </div>
      </div>
    </div>
  );
}

// ----------------------------------------------------- Upload (unchanged)
interface UploadContext {
  tempId: string;
  blobUrl: string;
}
interface UploadArgs {
  file: File;
}
function useUploadPhoto(onDone: () => void) {
  const qc = useQueryClient();
  return useMutation<unknown, Error, UploadArgs, UploadContext>({
    onMutate: async ({ file }) => {
      const tempId = `tmp_${crypto.randomUUID?.() ?? Math.random().toString(36).slice(2)}`;
      const blobUrl = URL.createObjectURL(file);
      await qc.cancelQueries({ queryKey: ['photo-grid'] });
      qc.setQueryData<PhotoGridResponse>(['photo-grid', null], (old) => ({
        photos: [
          { id: tempId, url: blobUrl, takenAt: Date.now(), pending: true },
          ...(old?.photos ?? []),
        ],
        nextCursor: old?.nextCursor ?? null,
      }));
      return { tempId, blobUrl };
    },
    mutationFn: ({ file }) => uploadPhoto(file, { type: 'family' }),
    onSuccess: (_data, _file, ctx) => {
      if (ctx) URL.revokeObjectURL(ctx.blobUrl);
      void qc.invalidateQueries({ queryKey: ['photo-grid'] });
      void qc.invalidateQueries({ queryKey: ['photo-siblings'] });
      onDone();
    },
    onError: (_err, _file, ctx) => {
      if (!ctx) return;
      qc.setQueryData<PhotoGridResponse>(['photo-grid', null], (old) => ({
        photos: (old?.photos ?? []).filter((p) => p.id !== ctx.tempId),
        nextCursor: old?.nextCursor ?? null,
      }));
      URL.revokeObjectURL(ctx.blobUrl);
    },
  });
}
